import React from "react";
import escapeStringRegexp from "escape-string-regexp";
import { nl2br as _nl2br } from "../utils/nl2br";

type Props = {
  nl2br?: boolean;
  elementStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  search?: string | RegExp;
  children: React.ReactNode;
};

class Highlighter extends React.PureComponent<Props> {
  count = 0;

  render() {
    return <span style={this.props.style}>{this.renderElement()}</span>;
  }

  renderElement() {
    const { children, nl2br } = this.props;

    if (this.isScalar() && this.hasSearch()) {
      const search = this.getSearch();
      return this.highlightChildren(children, search);
    }

    return nl2br ? _nl2br(children) : children;
  }

  isScalar() {
    return /string|number|boolean/.test(typeof this.props.children);
  }

  hasSearch() {
    return this.props.search != null && this.props.search !== "";
  }

  getSearch() {
    if (this.props.search instanceof RegExp) {
      return this.props.search;
    }

    let search = this.props.search;
    if (typeof this.props.search === "string") {
      search = escapeStringRegexp(search);
    }

    return new RegExp(search, "i");
  }

  getMatchBoundaries(subject, search) {
    const matches = search.exec(subject);

    if (matches) {
      return {
        first: matches.index,
        last: matches.index + matches[0].length
      };
    }
  }

  highlightChildren(subject, search: RegExp) {
    const children = [];
    let remaining = subject;

    while (remaining) {
      if (!search.test(remaining)) {
        children.push(this.renderPlain(remaining));
        return children;
      }

      const boundaries = this.getMatchBoundaries(remaining, search);

      if (boundaries.first === 0 && boundaries.last === 0) {
        // Regex zero-width match
        return children;
      }

      // Capture the string that leads up to a match...
      const nonMatch = remaining.slice(0, boundaries.first);
      if (nonMatch) {
        children.push(this.renderPlain(nonMatch));
      }

      // Now, capture the matching string...
      const match = remaining.slice(boundaries.first, boundaries.last);
      if (match) {
        children.push(this.renderHighlight(match));
      }

      // And if there's anything left over, recursively run this method again.
      remaining = remaining.slice(boundaries.last);
    }

    return children;
  }

  renderPlain(string) {
    this.count++;
    const children = this.props.nl2br ? _nl2br(string) : string;

    return React.createElement("span", {
      key: this.count,
      style: this.props.elementStyle,
      children
    });
  }

  renderHighlight(string) {
    this.count++;
    const children = this.props.nl2br ? _nl2br(string) : string;

    return React.createElement("mark", {
      key: this.count,
      className: "highlight",
      style: this.props.elementStyle,
      children
    });
  }
}

export const hl = (text: string, search: string) =>
  typeof search === "string" ? <Highlighter search={search}>{text}</Highlighter> : text;

export default Highlighter;
